import { DialogRef as CdkDialogRef } from "@angular/cdk/dialog";
import { Observable } from "rxjs";

import { isMobileBottomSheetPane } from "../helpers/mobile-bottom-sheet.helper";
import { DialogCloseAction, type DialogClosePayload } from "../models/dialog-close-action.model";

type CdkDialogRefLike = CdkDialogRef<unknown, unknown>;

const closingListeners = new WeakMap<CdkDialogRefLike, Set<() => void>>();
const closingInProgress = new WeakSet<CdkDialogRefLike>();

const PANEL_CLOSING_CLASS = "azm-dialog-closing";
const BACKDROP_CLOSING_CLASS = "azm-backdrop-closing";

const DIALOG_EXIT_MS = 150;
const DIALOG_SLIDE_EXIT_MS = 225;
const DIALOG_SHEET_EXIT_MS = 280;

export function notifyDialogClosingBeforeClose(cdkRef: CdkDialogRefLike): void {
	const listeners = closingListeners.get(cdkRef);
	if (!listeners?.size) {
		return;
	}

	for (const listener of listeners) {
		listener();
	}

	closingListeners.delete(cdkRef);
}

function prefersReducedMotion(): boolean {
	return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function getExitDurationMs(pane: HTMLElement): number {
	if (isMobileBottomSheetPane(pane)) {
		return DIALOG_SHEET_EXIT_MS;
	}

	if (pane.classList.contains("dialog-start") || pane.classList.contains("dialog-end")) {
		return DIALOG_SLIDE_EXIT_MS;
	}

	return DIALOG_EXIT_MS;
}

export function closeCdkDialogWithAnimation<R = unknown>(cdkRef: CdkDialogRef<R, unknown>, result?: R): void {
	const key = cdkRef as CdkDialogRefLike;

	if (closingInProgress.has(key)) {
		return;
	}

	closingInProgress.add(key);

	const finish = (): void => {
		notifyDialogClosingBeforeClose(key);
		cdkRef.close(result);
	};

	const overlayRef = cdkRef.overlayRef;
	const pane = overlayRef?.overlayElement ?? null;
	const backdrop = overlayRef?.backdropElement ?? null;

	if (!pane || prefersReducedMotion()) {
		finish();
		return;
	}

	pane.classList.add(PANEL_CLOSING_CLASS);
	backdrop?.classList.add(BACKDROP_CLOSING_CLASS);

	const container = pane.querySelector<HTMLElement>(".cdk-dialog-container");
	const durationMs = getExitDurationMs(pane);
	let settled = false;

	const settle = (): void => {
		if (settled) {
			return;
		}

		settled = true;
		container?.removeEventListener("animationend", onAnimationEnd);
		window.clearTimeout(fallbackTimer);
		finish();
	};

	const onAnimationEnd = (event: AnimationEvent): void => {
		if (event.target !== container) {
			return;
		}

		settle();
	};

	container?.addEventListener("animationend", onAnimationEnd);
	const fallbackTimer = window.setTimeout(settle, durationMs + 50);
}

export class AzmDialogRef<T = object, R = boolean | DialogCloseAction | string | undefined> {
	constructor(private readonly cdkRef: CdkDialogRef<R, T>) {}

	get componentInstance(): T | null {
		return this.cdkRef.componentInstance ?? null;
	}

	get id(): string {
		return this.cdkRef.id;
	}

	onClosing(callback: () => void): void {
		const key = this.cdkRef as CdkDialogRefLike;
		const listeners = closingListeners.get(key) ?? new Set<() => void>();
		listeners.add(callback);
		closingListeners.set(key, listeners);
	}

	close(result?: R | DialogCloseAction | boolean | DialogClosePayload | null): void {
		closeCdkDialogWithAnimation(this.cdkRef as CdkDialogRef<R, unknown>, result as R);
	}

	afterClosed(): Observable<R | undefined> {
		return this.cdkRef.closed;
	}

	beforeClosed(): Observable<R | undefined> {
		return this.afterClosed();
	}

	addPanelClass(classes: string | string[]): this {
		this.cdkRef.addPanelClass(classes);
		return this;
	}

	removePanelClass(classes: string | string[]): this {
		this.cdkRef.removePanelClass(classes);
		return this;
	}

	updateSize(width?: string, height?: string): this {
		this.cdkRef.updateSize(width, height);
		return this;
	}

	updatePosition(): this {
		this.cdkRef.updatePosition();
		return this;
	}
}
